import { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import ComposeInternalMessage from "../components/ComposeInternalMessage";
import { getInternalMessages, markInternalMessageRead, acceptInternalMessage, declineInternalMessage, replyToInternalMessage } from "../api";
import { toast } from "../components/Toast";
import "./InternalMessagesPage.css";

export default function InternalMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);

  const departmentData = JSON.parse(localStorage.getItem("department")) || {};
  const isSuperuser = departmentData.is_superuser || false;

  async function loadMessages() {
    setLoading(true);
    try {
      const data = await getInternalMessages();
      setMessages(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadMessages();
  }, []);

  function handleSent() {
    setShowCompose(false);
    loadMessages();
  }

  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ka-GE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  async function handleRsvp(action) {
    if (!selectedMsg) return;
    setRsvpLoading(true);
    try {
      const updated = action === "accept"
        ? await acceptInternalMessage(selectedMsg.id)
        : await declineInternalMessage(selectedMsg.id);
      setSelectedMsg(updated);
      setMessages(prev => prev.map(m => m.id === updated.id ? updated : m));
    } catch (err) {
      toast(err.message, "error");
    }
    setRsvpLoading(false);
  }

  async function handleReply() {
    if (!selectedMsg || !replyText.trim()) return;
    setReplyLoading(true);
    try {
      const updated = await replyToInternalMessage(selectedMsg.id, replyText);
      setSelectedMsg(updated);
      setMessages(prev => prev.map(m => m.id === updated.id ? updated : m));
      setReplyText("");
      toast("პასუხი გაიგზავნა", "success");
    } catch (err) {
      toast(err.message, "error");
    }
    setReplyLoading(false);
  }

  return (
    <div className="dash-root">
      <Header />
      <div className="dash-layout">
        <Sidebar activePage="internal" isSuperuser={isSuperuser} />

        <div className="im-content">
          <div className="im-header">
            <h2 className="im-title">შიდა კომუნიკაცია</h2>
            <button className="im-compose-btn" onClick={() => { setShowCompose(true); setSelectedMsg(null); }}>
              + ახალი შეტყობინება
            </button>
          </div>

          {showCompose && (
            <ComposeInternalMessage
              onSent={handleSent}
              onCancel={() => setShowCompose(false)}
            />
          )}

          {loading ? (
            <div className="im-loading">იტვირთება...</div>
          ) : messages.length === 0 ? (
            <div className="im-empty">შეტყობინებები არ არის</div>
          ) : (
            <div className="im-list">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`im-card ${selectedMsg?.id === msg.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedMsg(msg);
                    markInternalMessageRead(msg.id).catch(() => {});
                  }}
                >
                  <div className="im-card-top">
                    <span className={`im-type-badge ${msg.message_type}`}>
                      {msg.message_type === "meeting" ? "შეხვედრა" : "შეტყობინება"}
                    </span>
                    <span className="im-card-date">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="im-card-subject">{msg.subject}</div>
                  <div className="im-card-sender">
                    {msg.sender_name}
                    {msg.sent_to_all && <span className="im-all-badge">ყველა</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedMsg && !showCompose && (
            <div className="im-detail">
              <div className="im-detail-header">
                <span className={`im-type-badge ${selectedMsg.message_type}`}>
                  {selectedMsg.message_type === "meeting" ? "შეხვედრა" : "შეტყობინება"}
                </span>
                <h3>{selectedMsg.subject}</h3>
              </div>
              <div className="im-detail-meta">
                <span>გამომგზავნი: {selectedMsg.sender_name}</span>
                <span>{formatDate(selectedMsg.created_at)}</span>
              </div>
              {selectedMsg.meeting_datetime && (
                <div className="im-detail-meeting">
                  <div>თარიღი: {formatDate(selectedMsg.meeting_datetime)}</div>
                  {selectedMsg.meeting_duration_minutes && (
                    <div>ხანგრძლივობა: {selectedMsg.meeting_duration_minutes} წუთი</div>
                  )}
                  {selectedMsg.meeting_location && (
                    <div>ადგილი: {selectedMsg.meeting_location}</div>
                  )}
                </div>
              )}
              <div className="im-detail-body">{selectedMsg.body}</div>

              {/* RSVP for meetings */}
              {selectedMsg.message_type === "meeting" && (
                <div className="im-rsvp-section">
                  <div className="im-rsvp-actions">
                    <button
                      className={`im-rsvp-btn accept ${selectedMsg.my_rsvp === "accepted" ? "active" : ""}`}
                      onClick={() => handleRsvp("accept")}
                      disabled={rsvpLoading}
                    >
                      {selectedMsg.my_rsvp === "accepted" ? "✓ დადასტურებულია" : "დადასტურება"}
                    </button>
                    <button
                      className={`im-rsvp-btn decline ${selectedMsg.my_rsvp === "declined" ? "active" : ""}`}
                      onClick={() => handleRsvp("decline")}
                      disabled={rsvpLoading}
                    >
                      {selectedMsg.my_rsvp === "declined" ? "✗ უარყოფილია" : "უარყოფა"}
                    </button>
                  </div>

                  {(selectedMsg.accepted_names?.length > 0 || selectedMsg.declined_names?.length > 0) && (
                    <div className="im-rsvp-status">
                      {selectedMsg.accepted_names?.length > 0 && (
                        <div className="im-rsvp-group accepted">
                          <span className="im-rsvp-label">დაადასტურეს ({selectedMsg.accepted_names.length}):</span>
                          <span className="im-rsvp-names">{selectedMsg.accepted_names.join(", ")}</span>
                        </div>
                      )}
                      {selectedMsg.declined_names?.length > 0 && (
                        <div className="im-rsvp-group declined">
                          <span className="im-rsvp-label">უარყვეს ({selectedMsg.declined_names.length}):</span>
                          <span className="im-rsvp-names">{selectedMsg.declined_names.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedMsg.recipient_names && selectedMsg.recipient_names.length > 0 && (
                <div className="im-detail-recipients">
                  მიმღებები: {selectedMsg.recipient_names.join(", ")}
                </div>
              )}
              {selectedMsg.ai_generated && (
                <div className="im-ai-badge">AI-ით გენერირებული</div>
              )}

              {/* Replies thread */}
              {selectedMsg.replies?.length > 0 && (
                <div className="im-replies-section">
                  <h4 className="im-replies-title">პასუხები ({selectedMsg.replies.length})</h4>
                  {selectedMsg.replies.map((r) => (
                    <div key={r.id} className="im-reply-item">
                      <div className="im-reply-header">
                        <span className="im-reply-sender">{r.sender_name}</span>
                        <span className="im-reply-date">{formatDate(r.created_at)}</span>
                      </div>
                      <div className="im-reply-body">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply input */}
              <div className="im-reply-input-section">
                <textarea
                  className="im-reply-textarea"
                  placeholder="დაწერეთ პასუხი..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                />
                <button
                  className="im-reply-send-btn"
                  onClick={handleReply}
                  disabled={replyLoading || !replyText.trim()}
                >
                  {replyLoading ? "იგზავნება..." : "პასუხი"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
